import { useCallback, useEffect, useMemo, useState } from 'react'
import type { DateRange } from 'react-day-picker'
import { isFuture, format, parse } from 'date-fns'
import { CalendarIcon, Loader2 } from 'lucide-react'

import { useCampaignSelection } from '@/entities/campaign'
import { useGenerateChatReport, type ChatGrouping, type ChatReportResponse } from '@/entities/chatMetric'
import { CampaignSelector } from '@/components/features/CampaignSelector'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

function fmt(d: Date, f: string): string { return format(d, f) }

function parseHashParams(): { startDate?: string; endDate?: string; grouping?: ChatGrouping } {
  const hash = window.location.hash.slice(1)
  if (!hash) return {}
  const params = new URLSearchParams(hash)
  return {
    startDate: params.get('start') || undefined,
    endDate: params.get('end') || undefined,
    grouping: (params.get('grouping') as ChatGrouping) || undefined,
  }
}

function updateHashParams(startDate?: string, endDate?: string, grouping?: string) {
  const params = new URLSearchParams()
  if (startDate) params.set('start', startDate)
  if (endDate) params.set('end', endDate)
  if (grouping) params.set('grouping', grouping)
  const hash = params.toString()
  window.history.replaceState(null, '', hash ? `#${hash}` : window.location.pathname)
}

function parseDateString(s?: string): Date | undefined {
  if (!s) return undefined
  const parsed = parse(s, 'yyyy-MM-dd', new Date())
  return isNaN(parsed.getTime()) ? undefined : parsed
}

function num(v: number | undefined, digits = 0): string {
  if (v === undefined || v === null || isNaN(v)) return '-'
  return digits ? v.toFixed(digits) : String(Math.round(v))
}

function ChatReportTable({ report }: { report: ChatReportResponse }) {
  const rows = report.data
  if (!rows.length) {
    return <div className="text-center text-muted-foreground py-8">No data for selected period</div>
  }

  const label = (key: string) => {
    if (report.grouping === 'agent') return report.agentNames?.[key] || `Agent ${key}`
    return key || '(unspecified)'
  }

  const generalKeys = ['Total sessions', 'Closed sessions', 'Active sessions', 'Successful sessions', 'Success rate', 'Avg messages per session']
  const durationKeys = ['Avg session length', '<1min', '1-5min', '5-15min', '>=15min']
  const latencyKeys = ['Avg response latency', 'p50 response latency', 'p95 response latency']

  return (
    <Card>
      <CardHeader><h3 className="text-body font-semibold">Results by {report.grouping}</h3></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-detail">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4">Group</th>
                {generalKeys.map(k => <th key={k} className="text-right py-2 px-2">{k}</th>)}
                {durationKeys.map(k => <th key={k} className="text-right py-2 px-2">{k}</th>)}
                {latencyKeys.map(k => <th key={k} className="text-right py-2 px-2">{k}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.groupKey} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-medium">{label(r.groupKey)}</td>
                  {generalKeys.map(k => (
                    <td key={k} className="text-right py-2 px-2">
                      {k === 'Success rate' ? `${num((r.generalMetrics[k] ?? 0) * 100, 1)}%`
                        : num(r.generalMetrics[k], k === 'Avg messages per session' ? 1 : 0)}
                    </td>
                  ))}
                  {durationKeys.map(k => (
                    <td key={k} className="text-right py-2 px-2">
                      {k === 'Avg session length' ? `${num(r.durationMetrics[k], 1)}s` : num(r.durationMetrics[k])}
                    </td>
                  ))}
                  {latencyKeys.map(k => (
                    <td key={k} className="text-right py-2 px-2">{num(r.latencyMetrics[k], 2)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

export function ChatReports() {
  const { campaignId, setCampaignId, campaigns, campaignsLoading } = useCampaignSelection()

  const initialParams = useMemo(() => parseHashParams(), [])

  const [grouping, setGrouping] = useState<ChatGrouping | ''>(() => initialParams.grouping || '')
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const from = parseDateString(initialParams.startDate)
    const to = parseDateString(initialParams.endDate)
    return from && to ? { from, to } : undefined
  })

  const [report, setReport] = useState<ChatReportResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const generate = useGenerateChatReport()

  useEffect(() => {
    const s = dateRange?.from ? fmt(dateRange.from, 'yyyy-MM-dd') : undefined
    const e = dateRange?.to ? fmt(dateRange.to, 'yyyy-MM-dd') : undefined
    updateHashParams(s, e, grouping || undefined)
  }, [dateRange, grouping])

  const isFormComplete = useMemo(
    () => Boolean(dateRange?.from && dateRange?.to && campaignId && grouping),
    [dateRange, campaignId, grouping],
  )

  const handleGenerate = async () => {
    if (!campaignId || !dateRange?.from || !dateRange?.to || !grouping) return
    setError(null)
    try {
      const result = await generate.mutateAsync({
        campaignId,
        startDate: fmt(dateRange.from, 'yyyy-MM-dd'),
        endDate: fmt(dateRange.to, 'yyyy-MM-dd'),
        grouping: grouping as ChatGrouping,
      })
      setReport(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report')
      setReport(null)
    }
  }

  const handleDateSelect = useCallback((range?: DateRange) => setDateRange(range), [])
  const handleGroupingChange = useCallback((v: string) => setGrouping(v as ChatGrouping), [])

  return (
    <div className="w-full h-full p-6 overflow-y-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex-shrink-0">
          <h1 className="text-heading font-bold text-gray-900 mb-1">Custom Reports — Chat</h1>
          <p className="text-detail text-gray-600">Analyze text conversation performance by channel or agent</p>
        </div>
        <CampaignSelector
          campaigns={campaigns}
          selectedCampaignId={campaignId}
          onCampaignChange={setCampaignId}
          className="w-[400px]"
          disabled={campaignsLoading}
        />
      </div>

      <Card>
        <CardHeader></CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="text-body font-medium w-24">Date Range:</label>
                <div className="flex-1">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[300px] justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange?.from ? (
                          dateRange?.to ? (
                            <>{fmt(dateRange.from, 'LLL dd, y')} - {fmt(dateRange.to, 'LLL dd, y')}</>
                          ) : fmt(dateRange.from, 'LLL dd, y')
                        ) : <span>Pick a date range</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="range"
                        defaultMonth={dateRange?.to}
                        selected={dateRange}
                        onSelect={handleDateSelect}
                        numberOfMonths={2}
                        disabled={(d) => isFuture(d)}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="text-body font-medium w-24">Grouping:</label>
                <div className="flex-1">
                  <Select value={grouping} onValueChange={handleGroupingChange}>
                    <SelectTrigger className="w-[300px]">
                      <SelectValue placeholder="Select grouping" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="integration">Integration (channel)</SelectItem>
                      <SelectItem value="agent">Agent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-24"></div>
                <Button
                  onClick={handleGenerate}
                  disabled={generate.isPending || !isFormComplete}
                  className="w-full max-w-[200px]"
                >
                  {generate.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating Report...</>
                  ) : 'Generate Report'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Badge variant="destructive">Error</Badge>
              <span className="text-body text-destructive">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {report && <ChatReportTable report={report} />}
    </div>
  )
}
